import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.#calculateTruncation();
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize.bind(this));
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;
    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);
    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle window resize events
   */
  #handleResize() {
    this.#calculateTruncation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

/* Aromahpure: keep actual variant data untouched, shorten only visible 3-part numeric pack labels. */
function shortenVariantPillLabel(element) {
  if (!(element instanceof HTMLElement)) return;

  const originalLabel = (element.textContent || '').replace(/\s+/g, ' ').trim();
  if (!originalLabel || element.dataset.apVariantLabelFixed === originalLabel) return;

  const parts = originalLabel.split(/\s*\/\s*/).map((part) => part.trim());
  if (parts.length !== 3 || !/^\d+$/.test(parts[1])) return;

  element.dataset.apOriginalVariantLabel = originalLabel;
  element.dataset.apVariantLabelFixed = parts[1];
  element.textContent = parts[1];
}

function shortenVariantPillLabels(root = document) {
  if (!root || !root.querySelectorAll) return;

  if (root instanceof HTMLElement && root.matches('.tcg-card-variant, .tcg-variant-pill')) {
    shortenVariantPillLabel(root);
  }

  root.querySelectorAll('.tcg-card-variant, .tcg-variant-pill').forEach(shortenVariantPillLabel);
}

shortenVariantPillLabels();

const variantPillObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) shortenVariantPillLabels(node);
    });
  });
});

variantPillObserver.observe(document.documentElement, { childList: true, subtree: true });

/* Aromahpure: duplicate-theme-only correction for the Screw Jar header menu link. */
const SCREW_JAR_MENU_URL = 'https://aromahpure.com/collections/glass-jar-screw-candles';

function normalizeMenuLabel(value) {
  return (value || '').replace(/\s+/g, ' ').trim();
}

function isHeaderMenuAnchor(anchor) {
  return Boolean(anchor.closest('#header-group, header-component, header-drawer, .header-section'));
}

function fixScrewJarMenuAnchor(anchor) {
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (!isHeaderMenuAnchor(anchor)) return;
  if (normalizeMenuLabel(anchor.textContent) !== 'Screw Jar') return;

  anchor.setAttribute('href', SCREW_JAR_MENU_URL);
}

function fixScrewJarMenuLinks(root = document) {
  if (!root) return;

  if (root instanceof HTMLAnchorElement) {
    fixScrewJarMenuAnchor(root);
  }

  if (root.querySelectorAll) {
    root.querySelectorAll('a').forEach(fixScrewJarMenuAnchor);
  }
}

function initializeScrewJarMenuFix() {
  fixScrewJarMenuLinks(document);

  const menuObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          fixScrewJarMenuLinks(node);
        }
      });
    });
  });

  menuObserver.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeScrewJarMenuFix, { once: true });
} else {
  initializeScrewJarMenuFix();
}

document.addEventListener('click', (event) => {
  const anchor = event.target instanceof Element ? event.target.closest('a') : null;
  if (anchor) fixScrewJarMenuAnchor(anchor);
}, true);

/*
 * Aromahpure Closair reels enhancer:
 * - keep the 950ee reel in the middle instead of slot 1
 * - add Reelfy-like automatic smooth horizontal movement
 * - pause briefly for manual swipe/drag, then resume
 * - do not interfere with the existing exact-video modal click behavior
 */
function initializeClosairReelfyMotion() {
  if (window.location.pathname.replace(/\/$/, '') !== '/collections/closair') return;
  if (window.__AP_CLOSAIR_REEL_MOTION_V1__) return;

  const track = document.getElementById('ap-closair-native-reels-track');
  if (!track) return;

  window.__AP_CLOSAIR_REEL_MOTION_V1__ = true;

  const featuredUrlPart = '950ee1dd37af460ca299ddc4a68490e2';
  const featuredCard = track.querySelector(`.ap-closair-reel-card[data-ap-reel-url*="${featuredUrlPart}"]`);

  if (featuredCard) {
    const staticCards = Array.from(track.querySelectorAll('.ap-closair-reel-card'))
      .filter((card) => card.id !== 'ap-closair-old-reel-card' && card !== featuredCard);

    const referenceCard = staticCards[2] || document.getElementById('ap-closair-old-reel-card');
    if (referenceCard) {
      track.insertBefore(featuredCard, referenceCard);
    } else {
      track.appendChild(featuredCard);
    }

    featuredCard.dataset.apFeatured = 'true';
  }

  track.style.setProperty('scroll-behavior', 'smooth');
  track.style.setProperty('scroll-snap-type', 'x mandatory');

  let autoTimer = null;
  let resumeTimer = null;
  let pausedUntil = 0;

  const visibleCards = () =>
    Array.from(track.querySelectorAll('.ap-closair-reel-card')).filter((card) => !card.hidden);

  const stopAuto = () => {
    if (autoTimer) {
      window.clearInterval(autoTimer);
      autoTimer = null;
    }
  };

  const advance = () => {
    const modal = document.getElementById('ap-closair-reel-modal');
    if (modal?.classList.contains('is-open')) return;
    if (Date.now() < pausedUntil) return;

    const cards = visibleCards();
    if (cards.length < 2) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll < 6) return;

    const first = cards[0];
    const second = cards[1];
    const step = Math.max(
      90,
      second ? second.offsetLeft - first.offsetLeft : Math.round(first.getBoundingClientRect().width + 12)
    );

    const next = track.scrollLeft + step;

    if (next >= maxScroll - 8) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      track.scrollTo({ left: Math.min(next, maxScroll), behavior: 'smooth' });
    }
  };

  const startAuto = () => {
    stopAuto();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    autoTimer = window.setInterval(advance, 2400);
  };

  const pauseBriefly = (delay = 1500) => {
    pausedUntil = Date.now() + delay;
    stopAuto();
    if (resumeTimer) window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(startAuto, delay);
  };

  ['pointerdown', 'touchstart', 'wheel'].forEach((eventName) => {
    track.addEventListener(eventName, () => pauseBriefly(1700), { passive: true });
  });

  ['pointerup', 'touchend'].forEach((eventName) => {
    track.addEventListener(eventName, () => pauseBriefly(1100), { passive: true });
  });

  track.addEventListener('click', () => pauseBriefly(900), { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      window.setTimeout(startAuto, 250);
    } else {
      stopAuto();
    }
  });

  window.addEventListener('pageshow', () => window.setTimeout(startAuto, 300));
  window.addEventListener('resize', () => pauseBriefly(800));

  window.setTimeout(startAuto, 700);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeClosairReelfyMotion, { once: true });
} else {
  initializeClosairReelfyMotion();
}

/* Aromahpure: Closair floating video lives on Closair PDPs only. */
function initializeClosairPdpFloatingVideo() {
  const pagePath = window.location.pathname.replace(/\/$/, '');
  const closairProductPaths = new Set([
    '/products/hanger-air-freshener-pack-of-3-fragrances-floral-woody-aqua',
    '/products/aromahpure-closair-shoe-rack-freshener-aqua',
    '/products/aromahpure-closair-shoe-rack-freshener-woody',
    '/products/aromahpure-closair-shoe-rack-freshener-floral',
  ]);

  if (!closairProductPaths.has(pagePath)) return;
  if (window.__AP_CLOSAIR_PDP_FLOATING_VIDEO_V1__) return;
  window.__AP_CLOSAIR_PDP_FLOATING_VIDEO_V1__ = true;

  const style = document.createElement('style');
  style.id = 'ap-closair-pdp-floating-style';
  style.textContent = `
    @keyframes ap-closair-soft-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-3px); }
    }

    .ap-closair-video-widget {
      position: fixed;
      right: 12px;
      bottom: 86px;
      width: 100px;
      aspect-ratio: 9 / 16;
      z-index: 48;
      overflow: hidden;
      border-radius: 12px;
      background: #111;
      box-shadow: 0 9px 26px rgba(0,0,0,.24);
      cursor: grab;
      touch-action: none;
      user-select: none;
      -webkit-user-select: none;
      -webkit-tap-highlight-color: transparent;
      animation: ap-closair-soft-float 3.4s ease-in-out infinite;
      transition: width .24s ease, border-radius .24s ease, box-shadow .24s ease;
    }

    .ap-closair-video-widget.is-dragging {
      cursor: grabbing;
      animation: none;
      transition: none;
    }

    .ap-closair-video-widget video {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      background: #111;
      pointer-events: none;
    }

    .ap-closair-video-sound,
    .ap-closair-video-close {
      position: absolute;
      z-index: 3;
      border: 0;
      color: #fff;
      background: rgba(0,0,0,.68);
      backdrop-filter: blur(7px);
      -webkit-backdrop-filter: blur(7px);
      cursor: pointer;
      padding: 0;
      display: grid;
      place-items: center;
    }

    .ap-closair-video-close {
      top: 5px;
      right: 5px;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      font-size: 17px;
      line-height: 1;
    }

    .ap-closair-video-sound {
      left: 5px;
      bottom: 5px;
      width: 25px;
      height: 25px;
      border-radius: 50%;
      font-size: 12px;
      line-height: 1;
    }

    .ap-closair-video-backdrop {
      position: fixed;
      inset: 0;
      z-index: 49;
      background: rgba(0,0,0,.42);
      opacity: 0;
      pointer-events: none;
      transition: opacity .22s ease;
    }

    .ap-closair-video-backdrop.is-visible {
      opacity: 1;
      pointer-events: auto;
    }

    .ap-closair-video-widget.is-expanded {
      left: 50%;
      top: 50%;
      right: auto;
      bottom: auto;
      width: min(300px, 74vw);
      max-height: 76vh;
      border-radius: 17px;
      z-index: 51;
      cursor: default;
      touch-action: auto;
      animation: none;
      transform: translate(-50%, -50%);
      box-shadow: 0 18px 55px rgba(0,0,0,.34);
    }

    .ap-closair-video-widget.is-expanded .ap-closair-video-close {
      top: 8px;
      right: 8px;
      width: 32px;
      height: 32px;
      font-size: 23px;
    }

    .ap-closair-video-widget.is-expanded .ap-closair-video-sound {
      left: 8px;
      bottom: 8px;
      width: 34px;
      height: 34px;
      font-size: 16px;
    }

    @media (max-width: 749px) {
      .ap-closair-video-widget {
        right: 9px;
        bottom: 82px;
        width: 82px;
        border-radius: 10px;
      }

      .ap-closair-video-close {
        top: 4px;
        right: 4px;
        width: 20px;
        height: 20px;
        font-size: 15px;
      }

      .ap-closair-video-sound {
        left: 4px;
        bottom: 4px;
        width: 23px;
        height: 23px;
        font-size: 11px;
      }

      .ap-closair-video-widget.is-expanded {
        width: min(250px, 72vw);
        max-height: 72vh;
        border-radius: 15px;
      }
    }
  `;
  document.head.appendChild(style);

  const widget = document.createElement('div');
  widget.id = 'ap-closair-video-widget';
  widget.className = 'ap-closair-video-widget';
  widget.dataset.expanded = 'false';
  widget.innerHTML = `
    <button type="button" class="ap-closair-video-close" aria-label="Close video">×</button>
    <video
      id="ap-closair-video-player"
      src="https://cdn.shopify.com/videos/c/o/v/9ff08746124f4167ae8a3194a2a98560.mp4"
      autoplay
      muted
      loop
      playsinline
      preload="metadata"
    ></video>
    <button type="button" class="ap-closair-video-sound" aria-label="Unmute video">
      <span class="ap-closair-video-sound-icon" aria-hidden="true">🔇</span>
    </button>
  `;

  const backdrop = document.createElement('div');
  backdrop.id = 'ap-closair-video-backdrop';
  backdrop.className = 'ap-closair-video-backdrop';
  backdrop.setAttribute('aria-hidden', 'true');

  document.body.appendChild(widget);
  document.body.appendChild(backdrop);

  const video = widget.querySelector('#ap-closair-video-player');
  const soundButton = widget.querySelector('.ap-closair-video-sound');
  const soundIcon = widget.querySelector('.ap-closair-video-sound-icon');
  const closeButton = widget.querySelector('.ap-closair-video-close');

  if (!video || !soundButton || !closeButton) return;

  const drag = {
    active: false,
    moved: false,
    justDragged: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    startLeft: 0,
    startTop: 0,
  };

  let miniPosition = null;

  const syncSoundUI = () => {
    const muted = video.muted;
    soundButton.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
    if (soundIcon) soundIcon.textContent = muted ? '🔇' : '🔊';
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const rememberMiniPosition = () => {
    const rect = widget.getBoundingClientRect();
    miniPosition = { left: rect.left, top: rect.top };
  };

  const applyMiniPosition = () => {
    if (!miniPosition) return;
    const rect = widget.getBoundingClientRect();
    const maxLeft = Math.max(6, window.innerWidth - rect.width - 6);
    const maxTop = Math.max(6, window.innerHeight - rect.height - 6);
    widget.style.left = `${clamp(miniPosition.left, 6, maxLeft)}px`;
    widget.style.top = `${clamp(miniPosition.top, 6, maxTop)}px`;
    widget.style.right = 'auto';
    widget.style.bottom = 'auto';
  };

  const expandAndUnmute = () => {
    if (widget.classList.contains('is-expanded')) return;
    rememberMiniPosition();
    widget.style.left = '';
    widget.style.top = '';
    widget.style.right = '';
    widget.style.bottom = '';
    widget.classList.add('is-expanded');
    backdrop.classList.add('is-visible');
    widget.dataset.expanded = 'true';
    video.muted = false;
    video.play().catch(() => {});
    syncSoundUI();
  };

  const collapse = () => {
    if (!widget.classList.contains('is-expanded')) return;
    widget.classList.remove('is-expanded');
    backdrop.classList.remove('is-visible');
    widget.dataset.expanded = 'false';
    applyMiniPosition();
  };

  const dismiss = () => {
    video.pause();
    backdrop.remove();
    widget.remove();
    style.remove();
  };

  widget.addEventListener('pointerdown', (event) => {
    if (widget.classList.contains('is-expanded')) return;
    if (event.target.closest('.ap-closair-video-sound') || event.target.closest('.ap-closair-video-close')) return;

    const rect = widget.getBoundingClientRect();
    drag.active = true;
    drag.moved = false;
    drag.pointerId = event.pointerId;
    drag.startX = event.clientX;
    drag.startY = event.clientY;
    drag.startLeft = rect.left;
    drag.startTop = rect.top;

    widget.classList.add('is-dragging');
    widget.style.left = `${rect.left}px`;
    widget.style.top = `${rect.top}px`;
    widget.style.right = 'auto';
    widget.style.bottom = 'auto';

    try { widget.setPointerCapture(event.pointerId); } catch (e) {}
  });

  widget.addEventListener('pointermove', (event) => {
    if (!drag.active || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) drag.moved = true;
    if (!drag.moved) return;

    event.preventDefault();
    const rect = widget.getBoundingClientRect();
    const maxLeft = Math.max(6, window.innerWidth - rect.width - 6);
    const maxTop = Math.max(6, window.innerHeight - rect.height - 6);
    widget.style.left = `${clamp(drag.startLeft + dx, 6, maxLeft)}px`;
    widget.style.top = `${clamp(drag.startTop + dy, 6, maxTop)}px`;
  });

  const finishDrag = (event) => {
    if (!drag.active || event.pointerId !== drag.pointerId) return;
    drag.active = false;
    widget.classList.remove('is-dragging');

    if (drag.moved) {
      drag.justDragged = true;
      rememberMiniPosition();
      window.setTimeout(() => {
        drag.justDragged = false;
      }, 120);
    }

    try { widget.releasePointerCapture(event.pointerId); } catch (e) {}
  };

  widget.addEventListener('pointerup', finishDrag);
  widget.addEventListener('pointercancel', finishDrag);

  widget.addEventListener('click', (event) => {
    if (event.target.closest('.ap-closair-video-sound') || event.target.closest('.ap-closair-video-close')) return;
    if (drag.justDragged) return;
    if (!widget.classList.contains('is-expanded')) expandAndUnmute();
  });

  soundButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    video.muted = !video.muted;
    video.play().catch(() => {});
    syncSoundUI();
  });

  closeButton.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    dismiss();
  });

  backdrop.addEventListener('click', collapse);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') collapse();
  });

  window.addEventListener('resize', () => {
    if (!widget.isConnected || widget.classList.contains('is-expanded')) return;
    if (widget.style.left) {
      rememberMiniPosition();
      applyMiniPosition();
    }
  });

  video.addEventListener('volumechange', syncSoundUI);
  syncSoundUI();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeClosairPdpFloatingVideo, { once: true });
} else {
  initializeClosairPdpFloatingVideo();
}

export default ProductTitle;
